import React, { useState, useCallback } from 'react';
import { motion } from 'framer-motion';
import { Task } from '../../types';
import { useReducedMotion } from '../../hooks/useReducedMotion';
import { scaleIn, DURATIONS } from '../../utils/animations';
import styles from './TaskDetailModal.module.scss';

interface ThemeColors {
  appBackground: string;
  textColor: string;
  secondaryText: string;
  accentColor: string;
  cardBackground: string;
  cardBorder: string;
}

interface TaskDetailModalProps {
  task: Task;
  allTasks?: Task[]; // For overlap detection
  onSave: (task: Task) => void;
  onClose: () => void;
  onDelete?: (taskId: string) => void;
  onOpenLibrary?: () => void;
  isRecurringSeries?: boolean; // Kept for API compatibility; ignored
  themeColors?: ThemeColors; // Theme-aware styling
}

const EMOJI_OPTIONS = ['🪥', '👘', '🧹', '📚', '🧘', '🌙', '👕', '🥣', '🎒', '⏰', '🎨', '🏃', '🛁', '🍎', '📝', '🎮', '🎵', '🌟', '💤', '☀️', '🥞', '⚽', '🔢', '🔬', '✍️', '👨‍🍳', '🍕', '🧽', '🛠️', '🍪', '🥘', '🍽️', '👨‍👩‍👧', '📖'];

// A task's duration in minutes is chosen directly; its end time is derived from
// start + duration. Tasks no longer carry a hand-picked color — their color is a
// shade of the parent routine's color, assigned automatically.
const DURATION_OPTIONS = [5, 10, 15, 20, 30, 45, 60, 90, 120];

const formatDuration = (mins: number): string => {
  if (mins < 60) return `${mins} min`;
  const h = Math.floor(mins / 60);
  const m = mins % 60;
  return m === 0 ? `${h} hr` : `${h} hr ${m} min`;
};

const parseTimeToMinutes = (time: string): number => {
  if (!time) return 0;
  const [h, m] = time.split(':').map(Number);
  return h * 60 + m;
};

const minutesToTime = (mins: number): string => {
  const m = ((mins % 1440) + 1440) % 1440;
  return `${String(Math.floor(m / 60)).padStart(2, '0')}:${String(m % 60).padStart(2, '0')}`;
};

export function TaskDetailModal({
  task,
  allTasks = [],
  onSave,
  onClose,
  onDelete,
  onOpenLibrary,
  themeColors
}: TaskDetailModalProps) {
  const prefersReducedMotion = useReducedMotion();
  const [editedTask, setEditedTask] = useState<Task>(task);
  // Duration drives the end time. Seed from the incoming task's span (default 30m).
  const [durationMin, setDurationMin] = useState<number>(() => {
    const d = parseTimeToMinutes(task.end) - parseTimeToMinutes(task.start);
    return d > 0 ? d : 30;
  });

  const modalStyle: React.CSSProperties = themeColors ? {
    background: themeColors.appBackground,
    color: themeColors.textColor,
  } : {};

  const textStyle: React.CSSProperties = themeColors ? { color: themeColors.textColor } : {};
  const secondaryStyle: React.CSSProperties = themeColors ? { color: themeColors.secondaryText } : {};
  const [useKeyboard] = useState(false);
  const isNewTask = task.id.startsWith('task-');
  const [showOverlapWarning, setShowOverlapWarning] = useState(false);
  const [pendingSave, setPendingSave] = useState<Task | null>(null);

  const modalTitle = isNewTask ? 'New Task' : 'Edit Task';

  const handleChange = (field: keyof Task, value: any) => {
    setEditedTask(prev => ({ ...prev, [field]: value }));
  };

  // Changing the start keeps the duration fixed and slides the end along with it.
  const handleStartChange = (value: string) => {
    setEditedTask(prev => ({
      ...prev,
      start: value,
      end: minutesToTime(parseTimeToMinutes(value) + durationMin),
    }));
  };

  const handleDurationChange = (mins: number) => {
    setDurationMin(mins);
    setEditedTask(prev => ({
      ...prev,
      end: minutesToTime(parseTimeToMinutes(prev.start) + mins),
    }));
  };

  const checkForOverlap = useCallback((taskToCheck: Task): Task[] => {
    const startMin = parseTimeToMinutes(taskToCheck.start);
    const endMin = parseTimeToMinutes(taskToCheck.end);

    return allTasks.filter(t => {
      if (t.id === taskToCheck.id) return false;
      const tStart = parseTimeToMinutes(t.start);
      const tEnd = parseTimeToMinutes(t.end);
      return !(endMin <= tStart || startMin >= tEnd);
    });
  }, [allTasks]);

  const proceedWithSave = (taskToSave: Task) => {
    onSave(taskToSave);
    onClose();
  };

  const handleSave = () => {
    const timeRegex = /^([0-1]?[0-9]|2[0-3]):[0-5][0-9]$/;
    if (!timeRegex.test(editedTask.start)) {
      alert("Please enter a valid start time in HH:MM format (e.g. 08:30)");
      return;
    }

    // End is always derived from start + duration, so it can't precede start;
    // we just guard against a zero/negative duration.
    if (durationMin <= 0) {
      alert("Duration must be at least a few minutes.");
      return;
    }

    const overlappingTasks = checkForOverlap(editedTask);
    if (overlappingTasks.length > 0) {
      setPendingSave(editedTask);
      setShowOverlapWarning(true);
      return;
    }

    proceedWithSave(editedTask);
  };

  const handleOverlapConfirm = () => {
    if (pendingSave) {
      proceedWithSave(pendingSave);
    }
    setShowOverlapWarning(false);
    setPendingSave(null);
  };

  const handleDelete = () => {
    if (window.confirm(`Delete "${task.label}"?`)) {
      if (onDelete) {
        onDelete(task.id);
      }
      onClose();
    }
  };

  return (
    <motion.div 
      className={styles.overlay} 
      onClick={onClose}
      initial={prefersReducedMotion ? false : { opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: DURATIONS.micro }}
    >
      <motion.div 
        className={styles.modal} 
        onClick={(e) => e.stopPropagation()}
        initial={prefersReducedMotion ? false : "initial"}
        animate="animate"
        exit="exit"
        variants={scaleIn}
        style={modalStyle}
      >
        <div className={styles.header}>
          <h2 className={styles.title} style={textStyle}>{modalTitle}</h2>
          <button onClick={onClose} className={styles.closeBtn} style={secondaryStyle}>✕</button>
        </div>

        {/* Library button for new tasks */}
        {isNewTask && onOpenLibrary && (
          <button 
            className={styles.libraryButton}
            onClick={onOpenLibrary}
          >
            📚 Populate from Library
          </button>
        )}

        {/* 1. Label */}
        <div className={styles.field}>
          <label className={styles.label} style={secondaryStyle}>Task Name:</label>
          <input
            type="text"
            value={editedTask.label}
            onChange={(e) => handleChange('label', e.target.value)}
            className={styles.input}
            placeholder="Task name"
            style={textStyle}
          />
        </div>

        {/* 2. Start + Duration (end time is derived) */}
        <div className={styles.timeFields}>
          <div className={styles.field}>
            <label className={styles.label} style={secondaryStyle}>Start:</label>
            <input
              type={useKeyboard ? "text" : "time"}
              value={editedTask.start}
              onChange={(e) => handleStartChange(e.target.value)}
              className={styles.input}
              placeholder={useKeyboard ? "HH:MM" : undefined}
              style={textStyle}
            />
          </div>

          <div className={styles.field}>
            <label className={styles.label} style={secondaryStyle}>Duration:</label>
            <select
              value={durationMin}
              onChange={(e) => handleDurationChange(Number(e.target.value))}
              className={styles.select}
              style={textStyle}
            >
              {/* Keep a custom seed value selectable even if it's off-list */}
              {!DURATION_OPTIONS.includes(durationMin) && (
                <option value={durationMin}>{formatDuration(durationMin)}</option>
              )}
              {DURATION_OPTIONS.map(mins => (
                <option key={mins} value={mins}>{formatDuration(mins)}</option>
              ))}
            </select>
          </div>
        </div>

        <p className={styles.endHint} style={secondaryStyle}>
          Ends at {editedTask.end}
        </p>

        {/* 3. Icon */}
        <div className={styles.field}>
          <label className={styles.label}>Icon:</label>
          <select
            value={editedTask.icon}
            onChange={(e) => handleChange('icon', e.target.value)}
            className={styles.select}
          >
            {EMOJI_OPTIONS.map(emoji => (
              <option key={emoji} value={emoji}>{emoji}</option>
            ))}
          </select>
        </div>

        <div className={styles.actions}>
          {onDelete && !isNewTask && (
            <button 
              onClick={handleDelete} 
              className={styles.deleteBtn}
            >
              🗑️ Delete
            </button>
          )}
          <button onClick={onClose} className={styles.cancelBtn}>
            Cancel
          </button>
          <button onClick={handleSave} className={styles.saveBtn}>
            💾 Save
          </button>
        </div>
      </motion.div>

      {/* Overlap Warning Modal */}
      {showOverlapWarning && (
        <div className={styles.subOverlay} onClick={() => setShowOverlapWarning(false)}>
          <div className={styles.subModal} onClick={(e) => e.stopPropagation()}>
            <h3 className={styles.subTitle}>⚠️ Time Conflict Detected</h3>
            <p className={styles.subText}>
              This task overlaps with one or more existing tasks. 
              Are you sure you want to save it anyway?
            </p>
            <p className={styles.warningNote}>
              Only one task should be active at a time for best results.
            </p>
            
            <div className={styles.subActions}>
              <button 
                onClick={() => {
                  setShowOverlapWarning(false);
                  setPendingSave(null);
                }} 
                className={styles.cancelBtn}
              >
                Cancel
              </button>
              <button onClick={handleOverlapConfirm} className={styles.warningBtn}>
                Save Anyway
              </button>
            </div>
          </div>
        </div>
      )}
    </motion.div>
  );
}
